import { useMemo, useState } from 'react';

import type { ApBillListRow, CreateApPaymentRun } from '@dcc-erp/shared';

import { useApBills, useCreateApPaymentRun, useSubmitApPaymentRun } from '../../lib/queries/ap';
import { useCoa } from '../../lib/queries/coa';
import { AppModal } from '../AppModal';
import { WHT_TYPES } from './WhtLineEditor';

// Per-allocation editable row. amount defaults to the bill's outstanding; WHT is
// opt-in per allocation (the server recomputes the authoritative withholding).
interface AllocState {
  billId: string;
  vendorId: string;
  vendorName: string;
  amount: number;
  whtEnabled: boolean;
  whtIncomeType: string; // one of WHT_TYPES[].code
  whtRate: number;
}

function toNum(v: unknown): number {
  return typeof v === 'number' ? v : Number(v ?? 0);
}

// PaymentRunWizard — the 3-step cross-vendor payment-run builder.
//   Step 1: multi-select bills across vendors (pre-checked from `preselected`).
//   Step 2: per-allocation amount + opt-in WHT, grouped by vendor.
//   Step 3: cash account / date / note / e-WHT + grand totals → submit.
// Submit calls useCreateApPaymentRun (DRAFT) then useSubmitApPaymentRun (→ engine
// PENDING). The single entry point is the A4 "จ่ายเงินที่เลือก" button.
export function PaymentRunWizard({
  companyId,
  preselected,
  onClose,
}: {
  companyId: string;
  preselected?: ApBillListRow[];
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set((preselected ?? []).map((b) => b.id)),
  );
  const [allocs, setAllocs] = useState<Record<string, AllocState>>({});
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cashAccountCode, setCashAccountCode] = useState('');
  const [note, setNote] = useState('');
  const [whtChannel, setWhtChannel] = useState<'NORMAL' | 'E_WHT'>('NORMAL');

  const { data: bills } = useApBills(companyId, { status: 'POSTED' });
  const { data: coa } = useCoa(companyId);
  const createRun = useCreateApPaymentRun(companyId);
  const submitRun = useSubmitApPaymentRun(companyId);

  // Prefer the live POSTED list; fall back to the seeded rows (and the union so a
  // seeded bill that isn't in the current page is still selectable/visible).
  const billList: ApBillListRow[] = useMemo(() => {
    const base = bills ?? [];
    const seen = new Set(base.map((b) => b.id));
    const extra = (preselected ?? []).filter((b) => !seen.has(b.id));
    return [...base, ...extra];
  }, [bills, preselected]);

  // Build per-bill allocation rows from the current selection, preserving any
  // edits the user already made.
  function buildAllocs(ids: Set<string>): Record<string, AllocState> {
    const next: Record<string, AllocState> = {};
    for (const b of billList) {
      if (!ids.has(b.id)) continue;
      const outstanding = toNum(b.totalAmount) - toNum(b.paidAmount);
      next[b.id] = allocs[b.id] ?? {
        billId: b.id,
        vendorId: b.vendorId,
        vendorName: b.vendorName ?? b.vendorId,
        amount: outstanding,
        whtEnabled: false,
        whtIncomeType: 'SVC',
        whtRate: 3,
      };
    }
    return next;
  }

  function toggleBill(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Group the selected allocations by vendor for the step-2/3 per-vendor view.
  const byVendor = useMemo(() => {
    const m = new Map<string, AllocState[]>();
    for (const a of Object.values(allocs)) {
      const arr = m.get(a.vendorId) ?? [];
      arr.push(a);
      m.set(a.vendorId, arr);
    }
    return m;
  }, [allocs]);

  // Per-allocation WHT preview = pre-VAT portion of the entered amount × rate.
  // Advisory only — the server is authoritative on posting.
  function whtAmountFor(a: AllocState): number {
    if (!a.whtEnabled) return 0;
    const b = billList.find((x) => x.id === a.billId);
    const total = toNum(b?.totalAmount);
    const sub = toNum(b?.subtotal);
    const base = total > 0 ? (a.amount * sub) / total : 0;
    return Math.round(base * a.whtRate) / 100;
  }
  const grandGross = Object.values(allocs).reduce((s, a) => s + a.amount, 0);
  const grandWht = Object.values(allocs).reduce((s, a) => s + whtAmountFor(a), 0);

  async function handleSubmit() {
    const allocations: CreateApPaymentRun['allocations'] = Object.values(allocs).map((a) => ({
      billId: a.billId,
      vendorId: a.vendorId,
      amount: a.amount,
      ...(a.whtEnabled ? { whtIncomeType: a.whtIncomeType, whtRate: a.whtRate } : {}),
    }));
    const run = await createRun.mutateAsync({
      paymentDate,
      cashAccountCode,
      ...(note ? { note } : {}),
      whtChannel,
      allocations,
    });
    await submitRun.mutateAsync(run.id);
    onClose();
  }

  return (
    <AppModal title="จ่ายเป็นชุด (Payment Run)" onClose={onClose}>
      <div data-testid="run-wizard">
        {step === 1 && (
          <div data-testid="run-step-1">
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              เลือกใบสำคัญที่ต้องการจ่าย (เลือกข้ามผู้ขายได้ —
              ระบบจะแยกการจ่ายตามผู้ขายให้อัตโนมัติ)
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {billList.map((b) => (
                <label key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    aria-label={`เลือก ${b.voucherNo}`}
                    checked={selectedIds.has(b.id)}
                    onChange={() => toggleBill(b.id)}
                  />
                  <span>
                    {b.voucherNo} · {b.vendorName} · {toNum(b.totalAmount).toFixed(2)}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn primary"
                data-testid="run-next-1"
                disabled={selectedIds.size === 0}
                onClick={() => {
                  setAllocs(buildAllocs(selectedIds));
                  setStep(2);
                }}
              >
                ถัดไป
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="run-step-2">
            {[...byVendor.entries()].map(([vendorId, rows]) => (
              <div key={vendorId} style={{ marginBottom: 14 }}>
                <strong>{rows[0]?.vendorName}</strong>
                {rows.map((a) => (
                  <div
                    key={a.billId}
                    style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}
                  >
                    <input
                      type="number"
                      aria-label={`จำนวนเงิน ${a.billId}`}
                      value={a.amount}
                      onChange={(e) =>
                        setAllocs((p) => ({
                          ...p,
                          [a.billId]: { ...a, amount: Number(e.target.value) },
                        }))
                      }
                    />
                    <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label={`หักภาษี ${a.billId}`}
                        checked={a.whtEnabled}
                        onChange={(e) =>
                          setAllocs((p) => ({
                            ...p,
                            [a.billId]: { ...a, whtEnabled: e.target.checked },
                          }))
                        }
                      />
                      หัก ณ ที่จ่าย
                    </label>
                    {a.whtEnabled && (
                      <select
                        aria-label={`ประเภทเงินได้ ${a.billId}`}
                        value={a.whtIncomeType}
                        onChange={(e) => {
                          const t = WHT_TYPES.find((w) => w.code === e.target.value)!;
                          setAllocs((p) => ({
                            ...p,
                            [a.billId]: { ...a, whtIncomeType: t.code, whtRate: t.rate },
                          }));
                        }}
                      >
                        {WHT_TYPES.map((t) => (
                          <option key={t.code} value={t.code}>
                            {t.labelTh} ({t.rate}%)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn ghost" onClick={() => setStep(1)}>
                ย้อนกลับ
              </button>
              <button className="btn primary" data-testid="run-next-2" onClick={() => setStep(3)}>
                ถัดไป
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="run-step-3">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                บัญชีเงินสด/ธนาคาร
                <select
                  aria-label="บัญชีเงินสด/ธนาคาร"
                  value={cashAccountCode}
                  onChange={(e) => setCashAccountCode(e.target.value)}
                >
                  <option value="">— เลือกบัญชี —</option>
                  {(coa ?? [])
                    .filter((c) => c.type === 'ASSET' && c.isActive)
                    .map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} · {c.nameTh}
                      </option>
                    ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                วันที่จ่าย
                <input
                  type="date"
                  aria-label="วันที่จ่าย"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                หมายเหตุ
                <input
                  aria-label="หมายเหตุ"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  aria-label="e-WHT"
                  checked={whtChannel === 'E_WHT'}
                  onChange={(e) => setWhtChannel(e.target.checked ? 'E_WHT' : 'NORMAL')}
                />
                e-Withholding Tax
              </label>
            </div>
            <div className="tnum" data-testid="run-grand-total" style={{ marginTop: 14 }}>
              รวม {grandGross.toFixed(2)} · หัก {grandWht.toFixed(2)} · สุทธิ{' '}
              {(grandGross - grandWht).toFixed(2)}
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn ghost" onClick={() => setStep(2)}>
                ย้อนกลับ
              </button>
              <button
                className="btn primary"
                data-testid="run-submit"
                disabled={!cashAccountCode || createRun.isPending || submitRun.isPending}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                ส่งอนุมัติ
              </button>
            </div>
          </div>
        )}
      </div>
    </AppModal>
  );
}
